// Admin: view + merge duplicate-phone user groups.
import { useCallback, useEffect, useState } from "react";
import api from "@/api/client";
import { Users, ShieldCheck, Loader2, X, ArrowRightCircle, CheckCircle2, AlertTriangle, Wallet, Star, Package } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("ar", { dateStyle: "short" }); } catch { return ""; }
}

export default function DuplicatesModal({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [busyPhone, setBusyPhone] = useState(null);
  const [keepers, setKeepers] = useState({}); // { phone: keeper_user_id }
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users/duplicate-phones");
      setGroups(data.groups || []);
      // Preselect keeper = user with most orders (fallback: first)
      const next = {};
      for (const g of data.groups || []) {
        const sorted = [...g.users].sort((a, b) => (b.orders_count || 0) - (a.orders_count || 0));
        next[g._id] = sorted[0]?.id;
      }
      setKeepers(next);
    } catch (e) { setErr(e?.response?.data?.detail || "فشل تحميل التكرارات"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const mergeGroup = async (group) => {
    const keeperId = keepers[group._id];
    if (!keeperId) { setErr("اختر الحساب المُبقى أولاً"); return; }
    const others = group.users.filter((u) => u.id !== keeperId).map((u) => u.id);
    if (!others.length) return;
    const keeperName = group.users.find((u) => u.id === keeperId)?.name || "";
    if (!window.confirm(`دمج ${others.length} حساب في "${keeperName}"؟\nستُنقل كل الطلبات والرصيد والنقاط والرسائل — لا يمكن التراجع.`)) return;
    setBusyPhone(group._id); setErr(""); setMsg("");
    try {
      const { data } = await api.post("/admin/users/merge", { keep_user_id: keeperId, merge_user_ids: others });
      setMsg(`تم دمج ${data.merged_count} حساب في "${keeperName}" (${data.stats.orders} طلب • ${data.stats.wallet_added.toFixed(2)} د.ل • ${data.stats.points_added} نقطة).`);
      load();
    } catch (e) { setErr(e?.response?.data?.detail || "فشل الدمج"); }
    finally { setBusyPhone(null); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="duplicates-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}/>
      <div className="relative bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] flex flex-col talabya-shadow-float overflow-hidden">
        <div className="px-5 py-4 flex items-center justify-between bg-gradient-to-l from-primary-brand to-blue-500 text-white">
          <div className="flex items-center gap-2">
            <Users size={18}/>
            <div>
              <div className="font-black text-lg" style={{fontFamily:"Cairo"}}>الحسابات المكرّرة</div>
              <div className="text-[11px] text-white/85">{groups.length} مجموعة مع نفس الرقم</div>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center" data-testid="duplicates-close"><X size={16}/></button>
        </div>

        {msg && <div className="mx-5 mt-3 bg-green-50 text-green-700 px-3 py-2 rounded-lg text-sm" data-testid="merge-msg">{msg}</div>}
        {err && <div className="mx-5 mt-3 bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm" data-testid="merge-err">{err}</div>}

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="p-8 text-center text-sm text-[var(--text-secondary)] flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin"/> جاري التحميل...
            </div>
          ) : groups.length === 0 ? (
            <div className="p-10 text-center text-sm text-[var(--text-secondary)]">
              <ShieldCheck size={28} className="mx-auto mb-2 text-green-500"/>
              لا توجد حسابات مكرّرة. قاعدة البيانات نظيفة! ✨
            </div>
          ) : (
            groups.map((g) => (
              <div key={g._id} className="rounded-2xl border-2 border-yellow-200 bg-yellow-50/30 p-3" data-testid={`dup-group-${g._id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={16} className="text-yellow-600"/>
                  <span className="font-mono font-bold text-sm" dir="ltr">{g._id}</span>
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-black">{g.count} حسابات</span>
                </div>
                <div className="text-[10px] text-[var(--text-secondary)] mb-2">اختر الحساب الذي تريد الإبقاء عليه — البقية ستُدمج فيه.</div>
                <ul className="space-y-1.5">
                  {g.users.map((u) => {
                    const chosen = keepers[g._id] === u.id;
                    return (
                      <li
                        key={u.id}
                        onClick={() => setKeepers({ ...keepers, [g._id]: u.id })}
                        className={`p-2.5 rounded-xl cursor-pointer border-2 flex items-center gap-2 ${chosen ? "border-green-500 bg-green-50" : "border-transparent bg-white hover:border-primary-brand/30"}`}
                        data-testid={`dup-user-${u.id}`}
                      >
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${chosen ? "border-green-500 bg-green-500" : "border-gray-300"}`}>
                          {chosen && <CheckCircle2 size={12} className="text-white"/>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-black text-sm truncate">{u.name || "(بدون اسم)"}</span>
                            <span className="text-[10px] bg-[var(--surface-alt)] px-1.5 py-0.5 rounded-full font-bold">{u.role}</span>
                            {u.active === false && <span className="text-[10px] bg-red-50 text-red-500 px-1.5 py-0.5 rounded-full font-bold">معطّل</span>}
                          </div>
                          <div className="text-[10px] text-[var(--text-secondary)] flex items-center gap-3 flex-wrap mt-0.5">
                            {u.email && <span dir="ltr" className="truncate max-w-[180px]">{u.email}</span>}
                            <span className="flex items-center gap-0.5"><Package size={10}/> {u.orders_count} طلب</span>
                            <span className="flex items-center gap-0.5"><Wallet size={10}/> {u.wallet_balance?.toFixed(2)} د.ل</span>
                            <span className="flex items-center gap-0.5"><Star size={10}/> {u.loyalty_points} نقطة</span>
                            <span>{fmtDate(u.created_at)}</span>
                          </div>
                        </div>
                        {chosen && <span className="text-[10px] font-black bg-green-500 text-white px-2 py-0.5 rounded-full shrink-0">مُختار للإبقاء</span>}
                      </li>
                    );
                  })}
                </ul>
                <button
                  onClick={() => mergeGroup(g)}
                  disabled={busyPhone === g._id || !keepers[g._id] || g.count < 2}
                  className="mt-3 w-full py-2.5 rounded-xl bg-primary-brand text-white font-black text-sm disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid={`merge-btn-${g._id}`}
                >
                  {busyPhone === g._id ? <Loader2 size={14} className="animate-spin"/> : <ArrowRightCircle size={14}/>}
                  {busyPhone === g._id ? "جاري الدمج..." : `دمج ${g.count - 1} حساب → الحساب المختار`}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
